// @ts-strict-ignore
import React from 'react';
import styled from 'styled-components';
import { ColorProps } from 'styled-system';

import { CategoryHighlightBlockItem } from 'api/cms';
import { Box } from 'components/Box';
import Divider from 'components/Divider';
import { Icon } from 'components/Icon';
import { FilestackImage } from 'components/Image';
import { ConditionalLink, Link } from 'components/Link';
import { BodySmall, H3 } from 'components/Text';
import { colors, gutterWidth, transition } from 'theme';
import { trackClicked } from 'utils/analytics';

const ImageContainer = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  div {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StyledLink = styled(Link)<ColorProps>`
  color: ${(props) => props.color};
  svg {
    transition: transform ${transition};
    path,
    polyline {
      fill: currentColor;
    }
  }
  &:hover,
  &:focus {
    &,
    * {
      color: ${(props) => props.color};
      text-decoration: underline;
    }
    svg {
      transform: translateX(4px);
    }
  }
`;

type Props = {
  homePageUrl: string;
  category: CategoryHighlightBlockItem;
  blockIndex: number;
  pageUpdated: string;
};

export function CategoryHighlightBlockCategory({
  category,
  homePageUrl,
  blockIndex,
  pageUpdated,
  ...props
}: Props) {
  return (
    <Box
      data-qa-id={`category-highlight-${category.slug}`}
      p={[gutterWidth / 4, null, gutterWidth / 2]}
      width={[1, 1, 1 / 3]}
      flex="0 0 auto"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <ConditionalLink
        link={
          homePageUrl ? `/${homePageUrl}?categories=${category.slug}&pq=` : ''
        }
        onClick={() => {
          trackClicked('CMS Block', {
            block_position: blockIndex,
            block_type: 'Category Highlight',
            cta_type: 'Link',
            page_updated: pageUpdated,
          });
        }}
      >
        {category.image ? (
          <Box
            position="relative"
            height={145}
            width="100%"
            mb="12px"
            overflow="hidden"
            backgroundSize="cover"
            backgroundPosition="center"
            borderRadius={10}
          >
            <ImageContainer>
              <FilestackImage
                alt={category.image.altText}
                data={category.image}
                data-qa-id={`image-${category.slug}`}
                fit="crop"
              />
            </ImageContainer>
            <Box
              position="absolute"
              top={0}
              left={0}
              px={24}
              py={18}
              width="100%"
              height="100%"
              display="flex"
              alignItems="flex-end"
              bg="rgba(72, 72, 72, 0.35)"
            >
              <H3 my={0} color="white" styleAs="h2">
                {category.name}
              </H3>
            </Box>
          </Box>
        ) : (
          <H3 mb="4px">{category.name}</H3>
        )}
      </ConditionalLink>

      <BodySmall>{category.description}</BodySmall>

      <Divider light />

      {category.pages.map((page, index) => {
        const pageTitleWords = page.title.split(' ');
        return (
          <BodySmall
            styleAs="h5"
            lineHeight="1.5"
            mb="8px"
            key={`${page.id || index}`}
          >
            <StyledLink
              data-qa-id="articles-link"
              href={`/${page.url}`}
              onClick={() => {
                trackClicked('CMS Block', {
                  block_position: blockIndex,
                  block_type: 'Category Highlight',
                  cta_type: 'Link',
                  page_updated: pageUpdated,
                });
              }}
            >
              {pageTitleWords.slice(0, pageTitleWords.length - 1).join(' ')}{' '}
              <Box as="span" display="inline-flex">
                {pageTitleWords
                  .slice(pageTitleWords.length - 1, pageTitleWords.length)
                  .join(' ')}

                <Box display="inline-flex" ml="8px">
                  <Icon
                    initialColorName="todo-replace-me"
                    name="arrow-right"
                    size={24}
                  />
                </Box>
              </Box>
            </StyledLink>
          </BodySmall>
        );
      })}
      {homePageUrl && (
        <BodySmall styleAs="h5" lineHeight="1.5" mb="8px">
          <StyledLink
            color={colors.battleshipGrey}
            data-qa-id={`articles-link-${category.slug}`}
            href={`/${homePageUrl}?categories=${category.slug}&pq=`}
            onClick={() => {
              trackClicked('CMS Block', {
                block_position: blockIndex,
                block_type: 'Category Highlight',
                cta_type: 'Link',
                page_updated: pageUpdated,
              });
            }}
          >
            <Box as="span" display="inline-flex">
              {getText('See all')}

              <Box ml="8px" display="inline-flex">
                <Icon
                  initialColorName="todo-replace-me"
                  name="arrow-right"
                  size={24}
                />
              </Box>
            </Box>
          </StyledLink>
        </BodySmall>
      )}
    </Box>
  );
}
